import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';

import PropTypes from 'prop-types';

import Box from '@zenbusiness/zui/Box';

import * as Styled from './ChoiceFlow.style';
import FormStep from './FormStep';
import { resolveNextPage, computeProgress } from './state';
import { isFieldVisible } from './visibility';
import { useCustomForm } from '../../contexts/CustomFormContext';
import AnimationProgress from '../AnimationProgress/AnimationProgress';
import PoweredByVelo from '../PoweredByVelo/PoweredByVelo';

/**
 * A server-driven multi-step flow component that renders a series of FormStep pages.
 * Each FormStep can show multiple fields with branching logic and server-resolved transitions.
 * Now integrates with CustomFormContext for centralized form state management.
 *
 * Features:
 * - Multi-step form workflows with dynamic branching
 * - Progress tracking and localStorage persistence via CustomFormContext
 * - Accessibility support with screen reader announcements
 * - Centralized form state management
 *
 * @param {object} props - Component props
 * @param {string} [props.title] - Optional title for the flow
 * @param {boolean} [props.progress=true] - Whether to show progress indicator
 * @param {string} [props.progressMode='pages'] - Progress mode: 'pages' or 'required_fields'
 * @param {string} [props.persistKey] - Optional localStorage key for persistence (passed to CustomFormContext)
 * @param {object} props.data - Flow data containing pages and start page
 * @param {Function} [props.onComponentClick] - Callback for component actions (follows assistant-ui pattern)
 * @param {boolean} [props.showPoweredByVelo=true] - Whether to show the Powered by Velo logo
 * @param {string} [props.submitButtonText] - Custom text for the submit button on the last step
 * @returns {React.ReactElement} The ChoiceFlow component
 */
function ChoiceFlow({
  title,
  progress = true,
  progressMode = 'pages',
  persistKey,
  data,
  onComponentClick,
  showPoweredByVelo = true,
  submitButtonText
}) {
  // Use CustomFormContext for form state management
  const {
    answers,
    isLoading,
    setAnswer,
    clearAnswer,
    clearError,
    setLoading,
    getError,
    registerPersistKey
  } = useCustomForm();

  // Register persistKey with CustomFormContext for dynamic persistence
  // This allows the provider to be placed at app root without knowing the key upfront
  useEffect(() => {
    if (persistKey) {
      registerPersistKey(persistKey);
    }
  }, [persistKey, registerPersistKey]);

  // Internal flow state (navigation, pages, etc.)
  const [currentPageId, setCurrentPageId] = useState(data?.startPageId);
  const [visitedPageIds, setVisitedPageIds] = useState([]);
  const flowInitiatedRef = useRef(false);
  const [flowError, setFlowError] = useState(null);
  const [branch, setBranch] = useState(null);

  // Reset to start page if current page becomes invalid
  useEffect(() => {
    if (data && currentPageId && !data.pages?.some((page) => page.id === currentPageId)) {
      console.warn('[ChoiceFlow] Invalid currentPageId detected, resetting to start page');
      setCurrentPageId(data.startPageId);
      setVisitedPageIds([]);
    }
  }, [data, currentPageId]);

  // Persist flow-specific state to localStorage (separate from form answers)
  useEffect(() => {
    if (persistKey && currentPageId) {
      try {
        // Only persist if there's no existing data
        const existingData = localStorage.getItem(`${persistKey}_flow`);

        if (existingData && !flowInitiatedRef.current) {
          flowInitiatedRef.current = true;
          return;
        }

        const flowState = {
          currentPageId,
          visitedPageIds,
          branch
        };
        localStorage.setItem(`${persistKey}_flow`, JSON.stringify(flowState));
      } catch (error) {
        console.warn('[ChoiceFlow] Failed to persist flow state:', error);
      }
    }
  }, [persistKey, currentPageId, visitedPageIds, branch]);

  // Load flow-specific state from localStorage on mount
  useEffect(() => {
    if (persistKey && data?.pages) {
      try {
        const savedFlowState = localStorage.getItem(`${persistKey}_flow`);
        if (savedFlowState) {
          const parsedFlowState = JSON.parse(savedFlowState);

          // Validate that the saved currentPageId exists in the current flow
          const pageExists = data.pages.some((page) => page.id === parsedFlowState.currentPageId);

          if (pageExists) {
            setCurrentPageId(parsedFlowState.currentPageId);
            setVisitedPageIds(parsedFlowState.visitedPageIds || []);
            setBranch(parsedFlowState.branch || null);
          } else {
            console.warn('[ChoiceFlow] Saved page ID not found in current flow, starting fresh');
            // Clear invalid localStorage data
            localStorage.removeItem(`${persistKey}_flow`);
          }
        }
      } catch (error) {
        console.warn('[ChoiceFlow] Failed to load persisted flow state:', error);
      }
    }
  }, [persistKey, data?.pages]);

  // Navigation handlers
  const handleBack = useCallback(() => {
    if (visitedPageIds.length === 0) {
      return; // Can't go back further
    }

    const previousPageId = visitedPageIds[visitedPageIds.length - 1];
    const newVisitedPageIds = visitedPageIds.slice(0, -1);

    // Clear downstream answers when going back
    const currentPageIndex = data?.pages?.findIndex((page) => page.id === previousPageId) || 0;
    const downstreamFieldIds = new Set();

    if (data?.pages) {
      for (let i = currentPageIndex + 1; i < data.pages.length; i += 1) {
        const page = data.pages[i];
        if (page.kind === 'form_step' && page.fields) {
          page.fields.forEach((field) => {
            downstreamFieldIds.add(field.id);
          });
        }
      }
    }

    // Clear downstream answers
    downstreamFieldIds.forEach((fieldId) => {
      clearAnswer(fieldId);
    });

    setCurrentPageId(previousPageId);
    setVisitedPageIds(newVisitedPageIds);
    setFlowError(null);
  }, [visitedPageIds, data, clearAnswer]);

  const handleContinue = useCallback(async () => {
    const currentPage = data?.pages?.find((page) => page.id === currentPageId);
    if (!currentPage) return;

    setLoading(true);
    setFlowError(null);

    try {
      const nextPageId = resolveNextPage(currentPage, answers);
      if (!nextPageId) {
        // Flow completed (next: null) - emit submission event
        if (onComponentClick) {
          await onComponentClick({
            type: 'choice_flow_submission',
            payload: {
              answers
            }
          });
        }
      }

      // Navigate to next page
      const newVisitedPageIds = visitedPageIds.includes(currentPageId)
        ? visitedPageIds
        : [...visitedPageIds, currentPageId];

      setCurrentPageId(nextPageId);
      setVisitedPageIds(newVisitedPageIds);
    } catch (error) {
      console.error('[ChoiceFlow] Navigation error:', error);
      setFlowError('Failed to navigate to next step');
    }

    setLoading(false);
  }, [data, currentPageId, answers, visitedPageIds, onComponentClick, setLoading]);

  const handleFieldChange = useCallback(
    (fieldId, value) => {
      setAnswer(fieldId, value);
      // Clear field error when user provides input
      if (getError(fieldId)) {
        clearError(fieldId);
      }
    },
    [setAnswer, getError, clearError]
  );

  // Current page and progress calculation
  const currentPage = useMemo(
    () => data?.pages?.find((page) => page.id === currentPageId),
    [data?.pages, currentPageId]
  );

  useEffect(() => {
    if (!currentPage && visitedPageIds.length > 0) {
      handleBack();
    }
  }, [currentPage, visitedPageIds, handleBack]);

  // Create a state-like object for progress calculation
  const flowState = useMemo(
    () => ({
      currentPageId,
      visitedPageIds,
      answers,
      isBusy: isLoading,
      error: flowError,
      branch
    }),
    [currentPageId, visitedPageIds, answers, isLoading, flowError, branch]
  );

  const progressInfo = useMemo(
    () => computeProgress(flowState, data, progressMode),
    [flowState, data, progressMode]
  );

  // Visibility helper
  const checkFieldVisibility = useCallback((field) => isFieldVisible(field, answers), [answers]);

  // Check if current page is the last step
  const isLastStep = useMemo(() => {
    if (!currentPage?.next) {
      return true;
    }
    return currentPage.next === null || currentPage.next.goto === null;
  }, [currentPage]);

  if (!data) {
    return (
      <Styled.ChoiceFlowRoot>
        <Styled.ErrorMessage>Flow data is missing</Styled.ErrorMessage>
      </Styled.ChoiceFlowRoot>
    );
  }

  // if (!currentPage) {
  //   console.error('[ChoiceFlow] Current page not found:', {
  //     currentPageId,
  //     availablePages: data.pages?.map((p) => p.id),
  //     startPageId: data.startPageId
  //   });

  //   return (
  //     <Styled.ChoiceFlowRoot>
  //       <Styled.ErrorMessage>
  //         Page &quot;{currentPageId}&quot; not found in flow. Available pages:{' '}
  //         {data.pages?.map((p) => p.id).join(', ')}
  //       </Styled.ErrorMessage>
  //     </Styled.ChoiceFlowRoot>
  //   );
  // }

  return (
    <Styled.ChoiceFlowRoot>
      {title && <Styled.FlowTitle>{title}</Styled.FlowTitle>}

      {progress && (
        <Styled.ProgressContainer>
          <Styled.ProgressText size="sm" weight="bold">
            {progressMode === 'pages'
              ? `Question ${progressInfo.current} of ${progressInfo.total}`
              : `${progressInfo.current} of ${progressInfo.total} required fields completed`}
          </Styled.ProgressText>
          <Box style={{ marginTop: '8px' }}>
            <AnimationProgress
              ariaLabel={`Progress: ${progressInfo.percentage}% complete`}
              duration={0.8}
              height={4}
              value={progressInfo.percentage}
              variant="determinate"
            />
          </Box>
        </Styled.ProgressContainer>
      )}

      <Styled.FlowContent>
        {currentPage?.kind === 'form_step' ? (
          <FormStep
            answers={answers}
            canGoBack={visitedPageIds.length > 0}
            error={flowError}
            isFieldVisible={checkFieldVisibility}
            isLastStep={isLastStep}
            isLoading={isLoading}
            page={currentPage}
            submitButtonText={submitButtonText}
            onBack={handleBack}
            onContinue={handleContinue}
            onFieldChange={handleFieldChange}
          />
        ) : null}
      </Styled.FlowContent>
      {showPoweredByVelo && <PoweredByVelo />}
    </Styled.ChoiceFlowRoot>
  );
}

ChoiceFlow.propTypes = {
  title: PropTypes.string,
  progress: PropTypes.bool,
  progressMode: PropTypes.oneOf(['pages', 'required_fields']),
  persistKey: PropTypes.string,
  data: PropTypes.shape({
    startPageId: PropTypes.string.isRequired,
    pages: PropTypes.arrayOf(PropTypes.object).isRequired
  }).isRequired,
  onComponentClick: PropTypes.func,
  showPoweredByVelo: PropTypes.bool,
  submitButtonText: PropTypes.string
};

export default ChoiceFlow;
